import { CheckCircle, XCircle } from '@phosphor-icons/react';
import type { ImportProgress } from '@discovr/features';

type ImportProgressBarProps = ImportProgress;

/** Live-updates from the campaign's SSE stream as rows finish importing —
 *  the pulsing dot is a visual cue that this number moves on its own,
 *  distinct from the static counts everywhere else in the wizard. */
export function ImportProgressBar({ done, total, imported, rejected }: ImportProgressBarProps) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="rounded-xl border-2 border-(--border-brand-default) bg-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="size-3.5 animate-spin rounded-full border-2 border-(--brand-100) border-t-(--brand-500)" />
          <span className="text-body-sm-semibold text-foreground">Importing creators…</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="relative flex size-1.5">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-(--brand-400) opacity-75" />
            <span className="relative inline-flex size-1.5 rounded-full bg-(--brand-500)" />
          </span>
          <span className="text-caption-medium text-(--brand-600)">Live</span>
          <span className="text-body-sm-semibold text-(--brand-600)">
            {done} / {total}
          </span>
        </div>
      </div>

      <div
        role="progressbar"
        aria-label="Import progress"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-1.5 overflow-hidden rounded-full bg-(--brand-100)"
      >
        <div
          className="h-full rounded-full bg-linear-to-r from-(--brand-500) to-(--brand-400) transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="mt-2 flex gap-4 text-[11px]">
        <span className="inline-flex items-center gap-1 font-semibold text-success">
          <CheckCircle size={13} weight="fill" /> {imported} imported
        </span>
        {rejected > 0 && (
          <span className="inline-flex items-center gap-1 font-semibold text-destructive">
            <XCircle size={13} weight="fill" /> {rejected} rejected
          </span>
        )}
      </div>
    </div>
  );
}
